import React, {useMemo} from 'react';

import {SINGLE_DATASET_OUTPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {assert, assertAndContinue} from 'Asserts';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {DEFAULT_DATA_FRAME_ROW_LIMIT} from 'data-frames/graphql/GetDataFrame';
import DataFrameTable from 'data-frames/table/DataFrameTable';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import useDataFrameTableMode from 'data-frames/table/hooks/useDataFrameTableMode';
import {useControlledDataFrameTableSelectionState} from 'data-frames/table/hooks/useDataFrameTableSelectionState';
import getMetadataModalOverflowAction from 'data-frames/toolbar/toolbar-actions/getMetadataModalOverflowAction';
import {getCopyToClipboardAction} from 'data-frames/toolbar/toolbar-actions/renderCopyToClipboardOption';
import {getDownloadAsCSVAction} from 'data-frames/toolbar/toolbar-actions/renderDownloadAsCSVOption';
import {getToggleChipsAction} from 'data-frames/toolbar/toolbar-actions/renderToggleChipsOption';
import sortDataFrame from 'data-frames/util/sortDataFrame';
import {
  GetPersistedDataFrame,
  GetPersistedDataFrameVariables,
} from 'data-frames/wrapper/__generated/GetPersistedDataFrame';
import {
  GET_PERSISTED_DATA_FRAME_QUERY,
  PersistedDataFrameGraphQL,
} from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {assertPipelineStepRawDataIsKind, pipelineStepIsStepType} from 'pipelines/PipelineTypes';
import {getOutputStepDataRawDatas} from 'pipelines/StepDataUtils';
import useSimpleTableSortState from 'ui-library/simple-table/useSimpleTableSortState';

import getDataFrameWarning from './data-frame-view/getDataFrameWarning';
import DataViewContext, {combineDataViewWarnings, useDataViewContext} from './DataViewContext';
import DataViewPane from './DataViewPane';
import {FailedViewContent} from './FailedView';
import LoadingView from './LoadingView';

export interface DataFrameViewWithDataFrameProps {
  dataFrame: DataFrame;
  clientId: string;
  analyticsData?: {
    dataFrameId?: string;
  };
}

export interface DataFrameViewWithPersistedDataFrameProps {
  persistedDataFrame: PersistedDataFrameGraphQL;
}

export interface DataFrameViewWithDataFrameIdProps {
  dataFrameId: string;
}

export type DataFrameViewProps =
  | DataFrameViewWithDataFrameProps
  | DataFrameViewWithPersistedDataFrameProps
  | DataFrameViewWithDataFrameIdProps
  | Record<string, never>;

function isDataFrameViewWithDataFrame(props: DataFrameViewProps): props is DataFrameViewWithDataFrameProps {
  return props.hasOwnProperty('dataFrame');
}

function isDataFrameViewWithPersistedDataFrame(
  props: DataFrameViewProps
): props is DataFrameViewWithPersistedDataFrameProps {
  return props.hasOwnProperty('persistedDataFrame');
}

function isDataFrameViewWithDataFrameId(
  props: DataFrameViewProps
): props is DataFrameViewWithDataFrameIdProps {
  return props.hasOwnProperty('dataFrameId');
}

/**
 * Render view for data frames.
 *
 * Can be used in four ways:
 * 1. With a `dataFrame` prop: Renders the provided DataFrame object directly.
 * 2. With a `persistedDataFrame` prop: Renders the provided PersistedDataFrameGraphQL.
 * 3. With a `dataFrameId` prop: Queries and renders the data frame by ID.
 * 4. With no props: Uses analysisView from context.
 *
 * @example
 * // With DataFrame object
 * <DataFrameView dataFrame={dataFrameObject} clientId="my-client-id" />
 *
 * // With persisted data frame
 * <DataFrameView persistedDataFrame={persistedDataFrame} />
 *
 * // With data frame ID
 * <DataFrameView dataFrameId="df_abc123" />
 *
 * // With analysisView from DataViewContext
 * <DataFrameView />
 */
export default function DataFrameView(props: DataFrameViewProps = {}): JSX.Element {
  if (isDataFrameViewWithDataFrame(props)) {
    return (
      <DataFrameViewWithDataFrame
        dataFrame={props.dataFrame}
        clientId={props.clientId}
        analyticsData={props.analyticsData}
      />
    );
  }

  if (isDataFrameViewWithPersistedDataFrame(props)) {
    return <DataFrameViewWithPersistedDataFrame persistedDataFrame={props.persistedDataFrame} />;
  }

  if (isDataFrameViewWithDataFrameId(props)) {
    return <DataFrameViewWithDataFrameId dataFrameId={props.dataFrameId} />;
  }

  return <DataFrameViewWithAnalysisView />;
}

function DataFrameViewWithDataFrame({
  analyticsData: partialAnalyticsData,
  clientId,
  dataFrame,
}: DataFrameViewWithDataFrameProps): JSX.Element {
  const dataViewContextValue = useDataViewContext();
  const {
    actionInput,
    actionOrigin,
    dataFrameViewOptions: {
      dataFrameRenderOverrides,
      defaultTableMode = DataFrameTableMode.TEXT,
      headerMenuActions,
    },
    rowSelectionState: {selectedRowIndices, setSelectedRowIndices},
  } = dataViewContextValue;

  const analyticsData = useMemo(
    () => ({
      ...partialAnalyticsData,
      actionOrigin,
    }),
    [actionOrigin, partialAnalyticsData]
  );

  const {tableMode, setTableMode} = useDataFrameTableMode(clientId, defaultTableMode);

  const dataFrameTableSortOptions = useSimpleTableSortState(clientId);
  const sortedDataFrame = useMemo(
    () => sortDataFrame(dataFrame, dataFrameTableSortOptions.sortState, DataFrameTableMode.TEXT),
    [dataFrame, dataFrameTableSortOptions.sortState]
  );

  const selectionStateHookOutput = useControlledDataFrameTableSelectionState(
    sortedDataFrame,
    new Set([...selectedRowIndices].map((index) => String(index))),
    (selectedRowIds: Set<string>) => {
      setSelectedRowIndices(new Set([...selectedRowIds].map((id) => Number(id))));
    }
  );

  const newDataFrameViewContextValue = useMemo(
    () => ({
      ...dataViewContextValue,
      actions: {
        ...dataViewContextValue.actions,
        overflowActions: [
          ...dataViewContextValue.actions.overflowActions,
          getCopyToClipboardAction(sortedDataFrame, analyticsData, selectionStateHookOutput.selectedRowIds),
          getDownloadAsCSVAction(sortedDataFrame, analyticsData, selectionStateHookOutput.selectedRowIds),
          getToggleChipsAction(analyticsData, tableMode, setTableMode),
        ],
      },
      dataTestAttrs: {component: 'DataFrameView', element: 'root'},
    }),
    [analyticsData, dataViewContextValue, selectionStateHookOutput, setTableMode, sortedDataFrame, tableMode]
  );

  return (
    <DataViewContext.Provider value={newDataFrameViewContextValue}>
      <DataViewPane>
        <DataFrameTable
          actionOrigin={actionOrigin}
          dataFrame={sortedDataFrame}
          clientStateId={clientId}
          dataFrameRenderOverrides={dataFrameRenderOverrides}
          headerMenuOptions={headerMenuActions?.(actionInput) ?? undefined}
          mode={tableMode}
          renderIsExcludedColumn={false}
          selectionOptions={{allowRowSelection: true, ...selectionStateHookOutput}}
          sortOptions={dataFrameTableSortOptions}
        />
      </DataViewPane>
    </DataViewContext.Provider>
  );
}

function DataFrameViewWithPersistedDataFrame({
  persistedDataFrame,
}: DataFrameViewWithPersistedDataFrameProps): JSX.Element {
  const dataViewContextValue = useDataViewContext();

  const newDataViewContextValue = useMemo(() => {
    const {
      actions: {overflowActions, ...remainingActions},
      additionalMetadataContent,
    } = dataViewContextValue;

    const {allColumnsJson, dataFrameJson} = persistedDataFrame;

    return {
      ...dataViewContextValue,
      actions: {
        ...remainingActions,
        overflowActions: [
          ...overflowActions,
          getMetadataModalOverflowAction(persistedDataFrame, additionalMetadataContent),
        ],
      },
      actionInput: {persistedDataFrame},
      warning: combineDataViewWarnings(
        dataViewContextValue.warning,
        getDataFrameWarning({
          renderedColumns: dataFrameJson.columns.length,
          renderedRows: dataFrameJson.rows.length,
          totalColumns: allColumnsJson.length,
          totalRows: dataFrameJson.numRows ?? dataFrameJson.rows.length,
        })
      ),
    };
  }, [persistedDataFrame, dataViewContextValue]);

  return (
    <DataViewContext.Provider value={newDataViewContextValue}>
      <DataFrameViewWithDataFrame
        analyticsData={{dataFrameId: persistedDataFrame.id}}
        clientId={persistedDataFrame.id}
        dataFrame={persistedDataFrame.dataFrameJson}
      />
    </DataViewContext.Provider>
  );
}

function DataFrameViewWithDataFrameId({dataFrameId}: DataFrameViewWithDataFrameIdProps): JSX.Element {
  return (
    <BenchlingQuery<GetPersistedDataFrame, GetPersistedDataFrameVariables>
      query={GET_PERSISTED_DATA_FRAME_QUERY}
      renderLoading={() => <LoadingView />}
      variables={{dataFrameId, limit: DEFAULT_DATA_FRAME_ROW_LIMIT}}
    >
      {({dataFrame: persistedDataFrame}) =>
        isInaccessible(persistedDataFrame) ? (
          <FailedViewContent statusMessage="You do not have access to this data frame." />
        ) : (
          <DataFrameViewWithPersistedDataFrame persistedDataFrame={persistedDataFrame} />
        )
      }
    </BenchlingQuery>
  );
}

/** @package */
function DataFrameViewWithAnalysisView(): JSX.Element {
  const dataViewContextValue = useDataViewContext();
  const {analysisView, previewData} = dataViewContextValue;

  assert(analysisView != null, 'analysisView must be provided in context when using DataFrameView');

  const stepOutputReference = analysisView.lineage[analysisView.lineage.length - 1].stepOutputReference!;

  const dataFrame =
    previewData && pipelineStepIsStepType(PipelineStepType.TABLE_VIEW, 0, previewData.draftStep)
      ? getOutputStepDataRawDatas<PipelineStepType.TABLE_VIEW, 0>(
          previewData.draftStep,
          SINGLE_DATASET_OUTPUT_STEP_DATA_NAME
        )[0]
      : stepOutputReference.outputStepData.rawDatas[stepOutputReference.rawDataIndex!];

  assertPipelineStepRawDataIsKind(dataFrame, PipelineStepDataKind.DATASET);

  return (
    <BenchlingQuery<GetPersistedDataFrame, GetPersistedDataFrameVariables>
      query={GET_PERSISTED_DATA_FRAME_QUERY}
      renderLoading={() => <LoadingView />}
      variables={{dataFrameId: dataFrame.id, limit: DEFAULT_DATA_FRAME_ROW_LIMIT}}
    >
      {({dataFrame: persistedDataFrame}) =>
        assertAndContinue(
          !isInaccessible(persistedDataFrame),
          'Expected users to have access to all tables in analyses.'
        ) && !isInaccessible(persistedDataFrame) ? (
          <DataFrameViewWithPersistedDataFrame persistedDataFrame={persistedDataFrame} />
        ) : (
          <FailedViewContent statusMessage="You do not have access to this data frame." />
        )
      }
    </BenchlingQuery>
  );
}
